import React, { Suspense } from 'react';
import { Outlet } from 'react-router-dom';
import { PaymentProvider } from '../../contexts/PaymentContext';
import ErrorBoundary from '../ErrorBoundary';
import { Box, CircularProgress, Typography, Button } from '@mui/material';

/**
 * Payment Layout Wrapper
 * 
 * This component wraps all payment-related routes and provides the necessary
 * payment contexts.
 * 
 * PayPal SDK integration is now handled at the App level to avoid
 * multiple initializations and related errors.
 */
const PaymentLayoutWrapper = () => {
  // 自定义加载指示器
  const LoadingFallback = () => (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      p: 4,
      minHeight: '200px'
    }}>
      <CircularProgress size={40} sx={{ mb: 2 }} />
      <Typography variant="h6" color="text.secondary">
        正在加载支付系统...
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        请稍候，这可能需要几秒钟时间
      </Typography>
    </Box>
  );

  // 自定义错误UI
  const ErrorFallback = () => (
    <Box sx={{ 
      p: 4, 
      m: 2, 
      textAlign: 'center',
      border: '1px solid #f0f0f0',
      borderRadius: 2,
      bgcolor: '#fff9fa'
    }}>
      <Typography variant="h5" sx={{ mb: 2, color: '#d32f2f' }}>
        支付页面加载出错
      </Typography>
      <Typography variant="body1" sx={{ mb: 3 }}>
        很抱歉，支付系统暂时无法加载。请尝试刷新页面或使用不同的浏览器。
      </Typography>
      <Button 
        variant="contained"
        color="primary"
        onClick={() => window.location.reload()}
        sx={{ 
          bgcolor: '#0070ba',
          '&:hover': {
            bgcolor: '#005ea6'
          }
        }}
      >
        刷新页面
      </Button>
    </Box>
  );

  // 使用ErrorBoundary来捕获支付相关组件的错误
  return (
    <ErrorBoundary fallback={<ErrorFallback />} autoRetry={true} maxRetries={2}>
      <PaymentProvider>
        {/* 关键改动: 移除嵌套的PayPalProvider，使用App级别的全局PayPal配置 */}
        <Suspense fallback={<LoadingFallback />}>
          <Outlet />
        </Suspense>
      </PaymentProvider>
    </ErrorBoundary>
  );
};

export default PaymentLayoutWrapper;